import { JSX, ReactNode, ElementType } from 'react';

import MuiCardMedia, {
  CardMediaProps as MuiCardMediaProps,
} from '@mui/material/CardMedia';
import { SxProps, Theme } from '@mui/material/styles';
import classNames from 'classnames';

/**
 * Свойства компонента {@link CardMedia}
 */
export interface CardMediaProps extends MuiCardMediaProps {
  /**
   * Компонент для рендеринга (например, 'img', 'video', 'div').
   *
   * @defaultValue `'div'`
   */
  component?: ElementType;

  /**
   * URL изображения (если component="img").
   */
  image?: string;

  /**
   * Альтернатива image (HTML-стандарт).
   */
  src?: string;

  /**
   * Альтернативный текст для изображения (для accessibility).
   */
  alt?: string;

  /**
   * Показывать элементы управления (если component="video").
   */
  controls?: boolean;

  /**
   * Дочерние элементы (например, overlay или fallback-контент).
   */
  children?: ReactNode;

  /**
   * Стилизация через систему MUI `sx` (аналог inline-стилей, но с поддержкой темы).
   */
  sx?: SxProps<Theme>;

  /**
   * Ширина медиа-элемента.
   */
  width?: string | number;

  /**
   * Высота медиа-элемента.
   */
  height?: string | number;
}

/**
 * Кастомный компонент CardMedia.
 * Поддерживает:
 * - Изображения (`component="img"`), видео (`component="video"`), и другие медиа-типы.
 * - Стилизацию через `sx`.
 * - Кастомные overlay-элементы через `children`.
 *
 * @public
 * @param component - Компонент для рендеринга (например, 'img', 'video', 'div').
 * @param image - URL изображения (если component="img").
 * @param alt - Альтернативный текст для изображения (для accessibility).
 * @param children - Дочерние элементы (например, overlay или fallback-контент).
 * @param sx - Стилизация через систему MUI `sx` (аналог inline-стилей, но с поддержкой темы).
 * @param src - Альтернатива image (HTML-стандарт).
 * @param width - Ширина медиа-элемента.
 * @param height - Высота медиа-элемента.
 * @returns React-компонент CardMedia.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link CardMediaProps}
 */
export const CardMedia = ({
  component = 'div',
  image,
  alt,
  children,
  sx,
  src,
  width,
  height,
  ...rest
}: CardMediaProps): JSX.Element => {
  // Определяем источник изображения (приоритет у image, как в MUI)
  const mediaSrc = image || src;

  // Для component="img" добавляем стили, чтобы изображение заполняло контейнер
  const imgStyles: SxProps<Theme> | undefined =
    component === 'img'
      ? {
          width: width || '100%',
          height: height || '100%',
          objectFit: 'cover',
          ...(sx || {}),
        }
      : {
          width,
          height,
          ...(sx || {}),
        };

  return (
    <MuiCardMedia
      component={component}
      image={mediaSrc}
      alt={alt}
      className={classNames('aqa_card_media')}
      sx={imgStyles}
      {...rest}
    >
      {children}
    </MuiCardMedia>
  );
};
